'use client';

import { useEffect, useState } from 'react';

import { useRouter } from 'next/navigation';

import { useAuth } from '@/hooks/use-auth';
import { fromFetchError } from '@/lib/api/error';
import { getReferral } from '@/lib/referral';
import { useCreateCallbackRequestMutation } from '@/services/callbackRequestApi';
import { useGetMyProfileQuery } from '@/services/profileApi';
import {
  Button,
  Input,
  Spinner,
  Textarea,
  useToast,
} from '@holistic/components';
import {
  CheckCircle2,
  PhoneCall,
  Smartphone,
  X,
} from '@holistic/components/icons';
import { skipToken } from '@reduxjs/toolkit/query/react';

import type { TalepDevice, TalepRequestType } from './types';

type CallbackRequestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  requestType: TalepRequestType | null;
  device: TalepDevice | null;
  modelId: string | null;
};

const CallbackRequestModal = ({
  isOpen,
  onClose,
  requestType,
  device,
  modelId,
}: CallbackRequestModalProps) => {
  const router = useRouter();
  const toast = useToast();
  const { isLoggedIn } = useAuth();

  const { data: profile } = useGetMyProfileQuery(
    isLoggedIn ? undefined : skipToken
  );

  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);

  const [createCallback, { isLoading: isSubmitting }] =
    useCreateCallbackRequestMutation();

  // Profil yüklendiğinde ya da modal açıldığında otomatik doldur
  useEffect(() => {
    if (isOpen) {
      setIsSuccess(false);
      setErrors({});
      if (profile) {
        const name = [profile.firstName, profile.lastName]
          .filter(Boolean)
          .join(' ');
        if (name) setFullName(name);
        if (profile.phoneNumber) setPhoneNumber(profile.phoneNumber);
        if (profile.email) setEmail(profile.email);
      }
    }
  }, [isOpen, profile]);

  if (!isOpen || !requestType) return null;

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!fullName.trim())
      errs.fullName = 'Lütfen adınızı ve soyadınızı giriniz.';
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    if (!cleanPhone || cleanPhone.length < 10) {
      errs.phoneNumber = 'Lütfen geçerli bir telefon numarası giriniz.';
    }
    if (!description.trim()) {
      errs.description = 'Lütfen cihazdaki arızayı kısaca özetleyiniz.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const referral = getReferral();

    try {
      await createCallback({
        fullName: fullName.trim(),
        phoneNumber: phoneNumber.trim(),
        email: email.trim() || null,
        modelId: modelId || null,
        requestTypeId: requestType.id,
        description: description.trim(),
        utmSource: referral.utmSource ?? null,
        utmMedium: referral.utmMedium ?? null,
        utmCampaign: referral.utmCampaign ?? null,
        referrer: referral.referrer ?? null,
        landingPage: referral.landingPage ?? null,
      }).unwrap();

      setIsSuccess(true);
    } catch (err) {
      const error = fromFetchError(
        err,
        'Geri arama talebi oluşturulamadı, lütfen tekrar deneyiniz.'
      );
      toast.error(error.message);
    }
  };

  const handleClose = () => {
    setIsSuccess(false);
    onClose();
  };

  const handleGoToTaleplerim = () => {
    handleClose();
    router.push('/profilim/taleplerim');
  };

  return (
    <div className="animate-overlay-in fixed inset-0 z-50 flex items-end justify-center bg-gray-900/40 p-0 backdrop-blur-[2px] sm:items-center sm:p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white shadow-xl sm:rounded-3xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex size-9 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
              <PhoneCall className="size-4" />
            </span>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                {isSuccess ? 'Talebiniz Alındı' : 'Biz Sizi Arayalım'}
              </h3>
              <p className="text-xs text-gray-500">
                {isSuccess
                  ? 'Geri arama kaydınız başarıyla oluşturuldu.'
                  : 'Uzman teknisyenlerimiz sizi arayacaktır.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Kapat"
            className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isSuccess ? (
            <div className="flex flex-col items-center py-6 text-center">
              <div className="flex size-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <CheckCircle2 className="size-9" />
              </div>
              <h4 className="mt-4 text-lg font-bold text-gray-900">
                Geri Arama Talebiniz Alındı!
              </h4>
              <p className="mt-2 max-w-sm text-sm leading-relaxed text-gray-600">
                <strong className="text-gray-900">{phoneNumber}</strong> nolu
                numaranız üzerinden uzman ekibimiz en kısa sürede sizinle
                iletişime geçecektir.
              </p>
              <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50/70 p-3.5 text-xs text-gray-600">
                Uzman ekibimiz sizi{' '}
                <a
                  href="tel:+908503338444"
                  className="hover:text-primary font-bold text-gray-900 underline underline-offset-2"
                >
                  0850 333 8 444
                </a>{' '}
                numaralı hattımızdan arayacak veya aynı numaranın{' '}
                <a
                  href="https://wa.me/908503338444"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
                >
                  WhatsApp destek hattı
                </a>{' '}
                üzerinden iletişime geçecektir. Görüşme sonrasında talebiniz
                onaylandığında adres ve kargo bilgilerinizi içeren bağlantı
                tarafınıza SMS ile iletilecektir.
              </div>

              <div className="mt-6 flex w-full flex-col gap-2.5 sm:flex-row">
                {isLoggedIn ? (
                  <Button
                    type="button"
                    className="w-full flex-1 rounded-xl"
                    onClick={handleGoToTaleplerim}
                  >
                    Taleplerim Sayfasına Git
                  </Button>
                ) : (
                  <Button
                    type="button"
                    className="w-full flex-1 rounded-xl"
                    onClick={handleClose}
                  >
                    Tamam
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Device and type badge */}
              <div className="flex items-center gap-3 rounded-2xl border border-amber-200/80 bg-amber-50/60 p-3.5">
                <Smartphone className="size-5 shrink-0 text-amber-700" />
                <div className="min-w-0 flex-1 text-xs">
                  <p className="font-semibold text-amber-950">
                    {[device?.title, device?.subtitle]
                      .filter(Boolean)
                      .join(' • ') || 'Seçilen Cihaz'}
                  </p>
                  <p className="text-amber-800">
                    Arıza:{' '}
                    <span className="font-medium">{requestType.name}</span>{' '}
                    (İnceleme / Analiz Gerekli)
                  </p>
                </div>
              </div>

              <p className="text-xs leading-relaxed text-gray-500 italic">
                İletişim bilgilerinizi bırakın; sizi{' '}
                <a
                  href="tel:+908503338444"
                  className="hover:text-primary font-semibold text-gray-800 underline underline-offset-2"
                >
                  0850 333 8 444
                </a>{' '}
                numaralı hattımızdan “arayarak” veya aynı numaranın{' '}
                <a
                  href="https://wa.me/908503338444"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-emerald-700 underline underline-offset-2 hover:text-emerald-800"
                >
                  WhatsApp destek hattı
                </a>{' '}
                ile iletişime geçerek süreç ve tahmini fiyat hakkında bilgi
                vereceğiz.
              </p>

              {/* FullName */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-gray-700">
                  Adınız Soyadınız <span className="text-red-500">*</span>
                </label>
                <Input
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    if (errors.fullName) {
                      setErrors((prev) => ({ ...prev, fullName: '' }));
                    }
                  }}
                  placeholder="Adınız ve Soyadınız"
                  className="rounded-xl"
                  error={errors.fullName}
                />
              </div>

              {/* PhoneNumber & Email */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-700">
                    Telefon Numarası <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value);
                      if (errors.phoneNumber) {
                        setErrors((prev) => ({ ...prev, phoneNumber: '' }));
                      }
                    }}
                    placeholder="05XX XXX XX XX"
                    inputMode="tel"
                    className="rounded-xl"
                    error={errors.phoneNumber}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-gray-700">
                    E-posta Adresi{' '}
                    <span className="font-normal text-gray-400">
                      (Opsiyonel)
                    </span>
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ornek@eposta.com"
                    className="rounded-xl"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-gray-700">
                  Cihazdaki Sorun / Açıklama{' '}
                  <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    if (errors.description) {
                      setErrors((prev) => ({ ...prev, description: '' }));
                    }
                  }}
                  placeholder="Cihazınızda ne gibi bir sorun yaşıyorsunuz? Örn: Batarya çok hızlı tükeniyor, şişme yaptı veya kapanıyor..."
                  rows={3}
                  className="rounded-xl text-xs"
                />
                {errors.description && (
                  <p className="text-[11px] text-red-500">
                    {errors.description}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="mt-2 flex items-center justify-end gap-2.5">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl"
                  onClick={handleClose}
                >
                  Vazgeç
                </Button>
                <Button
                  type="submit"
                  className="rounded-xl"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner className="mr-1.5 size-4" />
                      İletiliyor...
                    </>
                  ) : (
                    <>
                      <PhoneCall className="mr-1.5 size-4" />
                      Biz Sizi Arayalım
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallbackRequestModal;
